import { useEffect, useState } from 'react';
import type { ScoreState } from '@aiscoreboard/contracts';
import { useT } from '../lib/i18n';

export function TablePreview({ state, showPreview }: { state: ScoreState; showPreview: boolean }) {
  const t = useT();
  const [shot, setShot] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [image, setImage] = useState<string | null>(null);
  const base = `/api/matches/${encodeURIComponent(state.matchId)}/restoration`;
  const restoring = state.paused && state.pauseReason === 'foul_and_miss';

  useEffect(() => {
    if (!showPreview) { setImage(null); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let objectUrl: string | null = null;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const response = await fetch(base, { cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error(t('match.referenceUnavailable'));
        const status = await response.json() as { available: boolean; shotId: string | null; live: boolean };
        if (!cancelled) { setShot(status.available ? status.shotId : null); setLive(status.live); }
        if (status.live) {
          const frame = await fetch(`${base}/current.jpg`, { cache: 'no-store', signal: controller.signal });
          if (frame.ok) {
            const url = URL.createObjectURL(await frame.blob());
            if (cancelled) { URL.revokeObjectURL(url); return; }
            const previous = objectUrl;
            objectUrl = url;
            setImage(url);
            if (previous) URL.revokeObjectURL(previous);
          }
        }
      } catch {
        if (!cancelled) { setLive(false); setShot(null); }
      } finally {
        if (!cancelled) timer = setTimeout(() => void poll(), 500);
      }
    };
    void poll();
    return () => { cancelled = true; controller.abort(); clearTimeout(timer); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [base, showPreview, t]);

  if (!showPreview || state.status !== 'in_progress' || (state.mode !== 'assisted' && !state.paused)) return null;
  return <div className="shrink-0 flex flex-col items-center gap-2 py-2" data-table-preview>
    {!live && <p className="text-xs text-white/60">{t('match.referenceUnavailable')}</p>}
    {restoring && <p className="text-xs text-sys-yellow">{t('match.restoreInstruction')}</p>}
    {live && image && <div className="relative max-h-[40vh] max-w-full overflow-hidden rounded">
      <img src={image} alt={t('match.tablePreview')} className="block max-h-[40vh] max-w-full object-contain" />
      {restoring && state.resetOverlay && <img src={`${base}/before.jpg?shot=${encodeURIComponent(shot ?? '')}`}
        alt={t('match.preShotReference')} className="absolute inset-0 h-full w-full object-contain pointer-events-none"
        style={{ opacity: 0.4 }} />}
    </div>}
  </div>;
}
